// src/components/CallingSheetTailwind.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import { getData, postData } from "../../services/FetchNodeservices";
import toast, { Toaster } from "react-hot-toast";

/**
 * CallingSheetTailwind.jsx
 * - Tailwind UI
 * - Bulk delete -> POST callingreport/delete-multiple { ids: [..] }
 * - Bulk export (selected or export filtered/all)
 * - Selection by id (not by name)
 * - Date parsing supports DD/MM/YYYY, DD-MM-YYYY and ISO
 * - Improved modal UI
 * - Replaced alerts with react-hot-toast
 */

export default function CallingSheetTailwind() {
  // data + UI state
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // form (react-hook-form)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // ---------------- fetch on mount ----------------
  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("USER"));
const user_id = user?.id;

const res = await postData("callingreport/list", { user_id });

      if (res.status) {
        const formattedRows = (res.data || []).map((item) => ({
          id: item.id,
          // backend already formats date as DD/MM/YYYY in your code; keep original for parsing
          date: item.date,
          candidateName: item.candidatename,
          status: item.status,
          email: item.emailid,
          contact: item.contactnumber,
          totalExp: item.totalexp,
          company: item.currentcompany,
          jobRole: item.jobrole,
          client: item.client,
          location: item.location,
          addedBy:
            `${item.created_by_first || ""} ${item.created_by_last || ""}`.trim() ||
            "Unknown",
          updatedBy:
            `${item.updated_by_first || ""} ${item.updated_by_last || ""}`.trim() ||
            "—",
        }));

        // Sort latest first by parsed date (safe) and secondary sort by ID descending
        formattedRows.sort((a, b) => {
          const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
          if (dateDiff !== 0) return dateDiff;
          return b.id - a.id;
        });
        setRows(formattedRows);
      } else {
        toast.error("Error fetching data: " + (res.message || "Unknown"));
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while fetching calling sheet");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- parsing helpers ----------------
  // Input may be "DD/MM/YYYY" (backend) or ISO; return timestamp
  function parseDateForSort(inp) {
    if (!inp) return 0;

    // 1) Try DD/MM/YYYY or DD-MM-YYYY or YYYY-MM-DD manually first
    // to prevent browser from parsing DD/MM/YYYY as MM/DD/YYYY
    const parts = String(inp).split(/[\/\-]/).map((p) => p.trim());
    if (parts.length >= 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD format
        const yyyy = parseInt(parts[0], 10);
        const mm = parseInt(parts[1], 10) - 1;
        const dd = parseInt(parts[2], 10);
        const d = new Date(yyyy, mm, dd);
        if (!isNaN(d.getTime())) return d.getTime();
      } else {
        // Assume DD/MM/YYYY or DD-MM-YYYY
        const dd = parseInt(parts[0], 10);
        const mm = parseInt(parts[1], 10) - 1;
        let yyyy = parseInt(parts[2], 10);
        if (yyyy < 100) {
          yyyy += 2000;
        }
        const d = new Date(yyyy, mm, dd);
        if (!isNaN(d.getTime())) return d.getTime();
      }
    }

    // 2) Direct ISO or recognized format
    const iso = new Date(inp);
    if (!isNaN(iso.getTime())) return iso.getTime();

    // 3) final fallback: attempt numeric parse
    const fallback = Date.parse(inp);
    if (!isNaN(fallback)) return fallback;

    return 0;
  }

  function formatToDDMMYYYY(inp) {
    if (!inp) return "—";
    // if already contains slash dd/mm/yyyy
    if (typeof inp === "string" && inp.split("/").length === 3) return inp;
    const ts = parseDateForSort(inp);
    if (!ts) return inp;
    const d = new Date(ts);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  // ---------------- filtering logic (memoized) ---------------
  const filteredRows = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();

    const now = new Date();
    let startDate = null;
    let endDate = null;
    if (dateRange === "Today") {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // today 00:00
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1); // tomorrow 00:00 (exclusive)
    } else if (dateRange === "Last 7 Days") {
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // include today
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 7);
    } else if (dateRange === "Last 30 Days") {
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30);
    }

    const filtered = rows.filter((r) => {
      // search across several fields
      const matchesSearch =
        !term ||
        (r.candidateName && r.candidateName.toLowerCase().includes(term)) ||
        (r.jobRole && r.jobRole.toLowerCase().includes(term)) ||
        (r.client && r.client.toLowerCase().includes(term)) ||
        (r.email && r.email.toLowerCase().includes(term));

      let inRange = true;
      if (startDate) {
        const rowTs = parseDateForSort(r.date);
        // If rowTs equals 0, exclude it from date-based filters to be safe
        inRange = rowTs !== 0 && rowTs >= startDate.getTime() && rowTs < endDate.getTime();
      }

      return matchesSearch && inRange;
    });

    // Keep original sort (rows already sorted)
    return filtered;
  }, [rows, searchTerm, dateRange]);

  // Reset selectedIds if selection not in filtered
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => filteredRows.some((r) => r.id === id)));
  }, [filteredRows]);

  // --------------- selection (by id) ----------------
  const handleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]));
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedIds(filteredRows.map((r) => r.id));
    else setSelectedIds([]);
  };

  const isAllSelected = selectedIds.length > 0 && selectedIds.length === filteredRows.length;

  // --------------- export ----------------
  // exportSelected = true → export selectedIds
  // exportSelected = false → export filteredRows
  const handleExport = (exportSelected = true) => {
    const dataToExport = exportSelected
      ? rows.filter((row) => selectedIds.includes(row.id))
      : filteredRows.slice();

    if (dataToExport.length === 0) {
      toast.error(exportSelected ? "Select at least one row to export." : "No rows to export.");
      return;
    }

    const headers = [
      "Date",
      "Candidate Name",
      "Status",
      "Email",
      "Contact",
      "Total Experience",
      "Current Company",
      "Job Role",
      "Client",
      "Location",
    ];
    const mapped = dataToExport.map((r) => [
      formatToDDMMYYYY(r.date),
      r.candidateName,
      r.status,
      r.email,
      r.contact,
      r.totalExp,
      r.company,
      r.jobRole,
      r.client,
      r.location,
    ]);

    try {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...mapped]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CallingSheet");
      XLSX.writeFile(wb, `CallingSheet_${Date.now()}.xlsx`);
      toast.success("Exported successfully");
      // clear selection after export (if we exported selected)
      if (exportSelected) setSelectedIds([]);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed");
    }
  };

  // --------------- bulk delete ----------------
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one record to delete.");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} record(s)? This cannot be undone.`)) return;

    const t = toast.loading("Deleting...");
    try {
      setActionLoading(true);
      const res = await postData("callingreport/delete-multiple", { ids: selectedIds });
      if (res.status) {
        toast.dismiss(t);
        toast.success(res.message || `${selectedIds.length} deleted.`);
        // refresh list
        await fetchList();
        setSelectedIds([]);
      } else {
        toast.dismiss(t);
        toast.error("Delete failed: " + (res.message || "Unknown"));
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.dismiss(t);
      toast.error("Server error while deleting records.");
    } finally {
      setActionLoading(false);
    }
  };

  // --------------- open view & edit ----------------
  const handleOpenView = (row) => {
    setSelectedRow(row);
    setViewModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    setSelectedRow(row);
    setEditModalOpen(true);
    // prefill form
    const isoDate = toInputDateValue(row.date);
    reset({
      date: isoDate,
      candidateName: row.candidateName,
      status: row.status,
      email: row.email,
      contact: row.contact,
      totalExp: row.totalExp,
      company: row.company,
      jobRole: row.jobRole,
      client: row.client,
      location: row.location,
    });
  };

  function toInputDateValue(dateStr) {
    // returns yyyy-mm-dd for input[type=date]
    const ts = parseDateForSort(dateStr);
    if (!ts) return "";
    const d = new Date(ts);
    return d.toISOString().split("T")[0];
  }

  // --------------- submit (create/update) ---------------
  const onSubmit = async (data) => {
    try {
      const user_id = localStorage.getItem("user_id") || null;
      const body = {
        date: data.date,
        candidatename: data.candidateName,
        status: data.status,
        emailid: data.email,
        contactnumber: data.contact,
        totalexp: data.totalExp,
        currentcompany: data.company,
        jobrole: data.jobRole,
        client: data.client,
        location: data.location,
        userid: user_id,
      };

      const mappedFields = {
        date: body.date,
        candidateName: body.candidatename,
        status: body.status,
        email: body.emailid,
        contact: body.contactnumber,
        totalExp: body.totalexp,
        company: body.currentcompany,
        jobRole: body.jobrole,
        client: body.client,
        location: body.location,
      };

      const t = toast.loading("Saving...");
      if (selectedRow && editModalOpen) {
        // update
        const response = await postData(`callingreport/update/${selectedRow.id}`, body);
        if (response.status) {
          const user = JSON.parse(localStorage.getItem("USER"));
          const userName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "You" : "You";

          const updatedFields = {
            ...mappedFields,
            updatedBy: userName
          };

          // update row locally and keep sorted
          setRows((prev) => {
            const updated = prev.map((r) => (r.id === selectedRow.id ? { ...r, ...updatedFields } : r));
            updated.sort((a, b) => {
              const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
              if (dateDiff !== 0) return dateDiff;
              return b.id - a.id;
            });
            return updated;
          });

          toast.dismiss(t);
          toast.success(response.message || "Updated successfully");
          setEditModalOpen(false);
          setSelectedRow(null);
        } else {
          toast.dismiss(t);
          toast.error("Error: " + (response.message || "Unknown"));
        }
      } else {
        // create new
        const response = await postData("callingreport/submit", body);
        if (response.status) {
          const user = JSON.parse(localStorage.getItem("USER"));
          const userName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "You" : "You";

          const newRow = {
            id: response.id || response.data?.insertId || Date.now(),
            ...mappedFields,
            addedBy: userName,
            updatedBy: "—",
          };

          setRows((prev) => {
            const updated = [newRow, ...prev];
            updated.sort((a, b) => {
              const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
              if (dateDiff !== 0) return dateDiff;
              return b.id - a.id;
            });
            return updated;
          });

          toast.dismiss(t);
          toast.success(response.message || "Submitted successfully");
          setEditModalOpen(false);
          setSelectedRow(null);
        } else {
          toast.dismiss(t);
          toast.error("Error: " + (response.message || "Unknown"));
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while submitting");
    }
  };

  // ---------------- UI ----------------
  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Calling Sheet
          </h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">
            Track client calls, candidates, follow-ups, and export Excel reports
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setEditModalOpen(true);
              setSelectedRow(null);
              reset();
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-white font-extrabold bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 text-xs"
          >
            New Candidate ➕
          </button>

          <button
            onClick={() => handleExport(true)}
            disabled={selectedIds.length === 0}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold transition duration-200 text-xs border ${
              selectedIds.length === 0 
                ? "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed" 
                : "bg-white text-indigo-600 border-indigo-200 shadow-sm hover:bg-indigo-50/50"
            }`}
          >
            Export Selected 📄
          </button>

          <button
            onClick={() => handleExport(false)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50 transition duration-200 text-xs"
          >
            Export Filtered 📄
          </button>

          <button
            onClick={handleBulkDelete}
            disabled={actionLoading || selectedIds.length === 0}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold transition duration-200 text-xs border ${
              selectedIds.length === 0 
                ? "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed" 
                : "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
            }`}
          >
            {actionLoading ? "Deleting..." : `Delete (${selectedIds.length})`}
          </button>
        </div>
      </div>

      {/* Toolbar & Filters Card */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Search Candidates</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
                placeholder="Search by name, role, client, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="w-full md:w-52">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
            >
              <option value="">All time</option>
              <option value="Today">Today</option>
              <option value="Last 7 Days">Last 7 Days</option>
              <option value="Last 30 Days">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table/Content area */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-indigo-100/20 overflow-hidden">
        <div className="w-full overflow-auto" style={{ maxHeight: "70vh" }}>
          {/* DESKTOP TABLE */}
          <table className="hidden md:table w-full table-fixed border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-[50px]">
                  <input
                    type="checkbox"
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="px-4 py-3.5 w-[110px]">Date</th>
                <th className="px-4 py-3.5">Candidate Name</th>
                <th className="px-4 py-3.5 w-[120px]">Status</th>
                <th className="px-4 py-3.5 w-[130px]">Contact</th>
                <th className="px-4 py-3.5">Job Role</th>
                <th className="px-4 py-3.5">Client</th>
                <th className="px-4 py-3.5">Added By</th>
                <th className="px-4 py-3.5">Last Updated</th>
                <th className="px-4 py-3.5 w-[130px] text-right">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-400 font-semibold">
                    Loading records...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-400 font-semibold">
                    No calling records found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50/30 transition text-gray-700"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => handleSelect(row.id)}
                      />
                    </td>
                    <td className="px-4 py-4 text-xs font-semibold text-gray-500">{formatToDDMMYYYY(row.date)}</td>
                    <td className="px-4 py-4 text-sm font-bold text-gray-900 truncate">{row.candidateName}</td>
                    <td className="px-4 py-4 text-xs">
                      <span className="bg-indigo-50 text-indigo-600 border border-indigo-100/50 px-2.5 py-1 rounded-full font-bold">
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs font-medium text-gray-600">{row.contact}</td>
                    <td className="px-4 py-4 text-xs font-semibold text-gray-800 truncate">{row.jobRole}</td>
                    <td className="px-4 py-4 text-xs font-semibold text-gray-800 truncate">{row.client}</td>
                    <td className="px-4 py-4 text-xs font-medium text-gray-500 truncate">{row.addedBy}</td>
                    <td className="px-4 py-4 text-xs font-medium text-gray-500 truncate">{row.updatedBy}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleOpenView(row)}
                          className="px-2.5 py-1 rounded-xl text-[11px] font-bold bg-[#f4f6fa]/60 text-gray-500 hover:bg-[#f4f6fa] hover:text-gray-700 transition"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleOpenEdit(row)}
                          className="px-2.5 py-1 rounded-xl text-[11px] font-bold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* MOBILE CARDS */}
          <div className="md:hidden px-4 py-4 space-y-3">
            {loading ? (
              <div className="text-center text-gray-400 font-semibold py-8">Loading...</div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center text-gray-400 font-semibold py-8">No records found</div>
            ) : (
              filteredRows.map((row) => (
                <div key={row.id} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500 mt-1"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => handleSelect(row.id)}
                      />
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{row.candidateName}</div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">
                          {formatToDDMMYYYY(row.date)} • {row.client}
                        </div>
                      </div>
                    </div>
                    <span className="bg-indigo-50 text-indigo-600 border border-indigo-100/50 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      {row.status}
                    </span>
                  </div>

                  <div className="text-xs text-gray-600 space-y-1 pl-7">
                    <div>
                      <strong className="text-gray-400">Role:</strong> {row.jobRole || "—"}
                    </div>
                    <div>
                      <strong className="text-gray-400">Contact:</strong> {row.contact || "—"}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-50 pl-7">
                    <button
                      onClick={() => handleOpenView(row)}
                      className="px-3 py-1.5 rounded-xl text-[10px] font-bold bg-[#f4f6fa]/60 text-gray-500"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleOpenEdit(row)}
                      className="px-3 py-1.5 rounded-xl text-[10px] font-bold bg-indigo-50 text-indigo-600"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* VIEW DETAILS MODAL */}
      {viewModalOpen && selectedRow && (
        <ModalTailwind onClose={() => { setViewModalOpen(false); setSelectedRow(null); }}>
          <div className="bg-white rounded-[32px] shadow-2xl p-8 max-w-xl w-full border border-gray-100 overflow-hidden relative">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-gray-900">Candidate Details</h3>
              <button 
                onClick={() => { setViewModalOpen(false); setSelectedRow(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-[#f4f6fa]/30 border border-gray-50 rounded-3xl p-5">
              <Detail label="Name" value={selectedRow.candidateName} />
              <Detail label="Email" value={selectedRow.email} />
              <Detail label="Contact" value={selectedRow.contact} />
              <Detail label="Status" value={selectedRow.status} />
              <Detail label="Job Role" value={selectedRow.jobRole} />
              <Detail label="Client" value={selectedRow.client} />
              <Detail label="Total Exp" value={selectedRow.totalExp} />
              <Detail label="Company" value={selectedRow.company} />
            </div>

            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => { setViewModalOpen(false); setSelectedRow(null); }} 
                className="bg-[#f4f6fa] text-gray-700 font-bold px-6 py-2.5 rounded-2xl hover:bg-gray-100 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </ModalTailwind>
      )}

      {/* EDIT / CREATE MODAL */}
      {editModalOpen && (
        <ModalTailwind onClose={() => { setEditModalOpen(false); setSelectedRow(null); reset(); }}>
          <div className="bg-white rounded-[32px] shadow-2xl p-8 max-w-2xl w-full border border-gray-100 overflow-hidden relative">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-black text-gray-900">{selectedRow ? "Edit Candidate" : "New Candidate"}</h3>
                {selectedRow && <p className="text-[10px] text-gray-400 mt-0.5">UID: {selectedRow.id}</p>}
              </div>
              <button 
                onClick={() => { setEditModalOpen(false); setSelectedRow(null); reset(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Date</label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    {...register("date", { required: "Date is required" })}
                  />
                  {errors.date && <div className="text-[10px] text-red-500 font-bold mt-1">{errors.date.message}</div>}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Candidate Name</label>
                  <input
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="Enter Candidate Name"
                    {...register("candidateName", { required: "Name required" })}
                  />
                  {errors.candidateName && <div className="text-[10px] text-red-500 font-bold mt-1">{errors.candidateName.message}</div>}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Status</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. Called, Interested"
                    {...register("status")} 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Email Address</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="name@domain.com"
                    {...register("email", {
                      required: "Email is required",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email address" }
                    })} 
                  />
                  {errors.email && <div className="text-[10px] text-red-500 font-bold mt-1">{errors.email.message}</div>}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Contact Number</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="10-digit number"
                    {...register("contact", {
                      required: "Contact required",
                      pattern: { value: /^[0-9]{10}$/, message: "Must be a 10-digit number" }
                    })} 
                  />
                  {errors.contact && <div className="text-[10px] text-red-500 font-bold mt-1">{errors.contact.message}</div>}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Total Experience</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. 3 Years"
                    {...register("totalExp")} 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Current Company</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. TCS"
                    {...register("company")} 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Job Role</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-805 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. React Developer"
                    {...register("jobRole")} 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Client Name</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. Morgan Consultancy"
                    {...register("client")} 
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Location</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    placeholder="e.g. Mumbai, Maharashtra"
                    {...register("location")} 
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button 
                  type="button" 
                  onClick={() => { setEditModalOpen(false); setSelectedRow(null); reset(); }} 
                  className="bg-[#f4f6fa] text-gray-700 font-bold px-6 py-2.5 rounded-2xl hover:bg-gray-100 transition text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-2.5 rounded-2xl shadow-lg shadow-indigo-100 transition text-sm"
                >
                  Save & Submit
                </button>
              </div>
            </form>
          </div>
        </ModalTailwind>
      )}
    </div>
  );
}

/* ----------------- small presentational helpers ----------------- */

function ModalTailwind({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl animate-fadeIn">{children}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm font-bold text-gray-800">{value || "—"}</div>
    </div>
  );
}

